import { type OptionProps } from '@amelio/ui-library';

import type { CycleEvaluation } from '@/types/cycle-evaluation/CycleEvaluation';

import { CycleEvaluationRow } from '@/components/CycleEvaluationRow/CycleEvaluationRow';
import { useAmelioTranslations } from '@/hooks';
import { useRevieweesFromAudience } from '@/hooks/cycle-instance/useRevieweesFromAudience';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getTypeBadgeLabelKey } from '@/pages/CycleEvaluation/Dashboard/cycleListRows';
import {
    getLegacyStatusLabelKey,
    getLegacyStatusTone,
} from '@/pages/CycleEvaluation/Dashboard/cycleStatus';
import { getLocalizedText } from '@/utils/localization';

interface CycleEvaluationRowWithResolvedTitleProps {
    cycleEvaluation: CycleEvaluation;
    actionMenuItems: (cycleEvaluation: CycleEvaluation) => OptionProps[];
    currentEvaluation?: CycleEvaluation;
    isUpdating: boolean;
    isRefetching: boolean;
    /** Optional callback when entire card row is clicked (e.g., navigate to cycle evaluation details) */
    onRowClick?: () => void;
}

export const CycleEvaluationRowWithResolvedTitle = ({
    cycleEvaluation,
    actionMenuItems,
    currentEvaluation,
    isUpdating,
    isRefetching,
    onRowClick,
}: CycleEvaluationRowWithResolvedTitleProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();
    const reviewees = useRevieweesFromAudience({ cycleEvaluation });

    const currentlyUpdating =
        cycleEvaluation.id === currentEvaluation?.id && (isUpdating || isRefetching);

    // The service casts the server response with no validation, so the type below promises two
    // fields the wire does not: an incomplete record must degrade, not crash the row.
    const title = replaceDynamicTags({
        text: getLocalizedText(cycleEvaluation.cycleQuestionnaire?.title),
        employee: reviewees[0],
    });

    return (
        <CycleEvaluationRow
            actionMenuItems={actionMenuItems(cycleEvaluation)}
            title={title}
            schedule={cycleEvaluation.cycleSchedule?.frequency}
            statusTone={getLegacyStatusTone(cycleEvaluation.status)}
            statusLabel={t(getLegacyStatusLabelKey(cycleEvaluation.status))}
            typeLabels={[t(getTypeBadgeLabelKey(cycleEvaluation.reviewType))]}
            className={currentlyUpdating ? 'animate-pulse' : ''}
            onRowClick={onRowClick}
        />
    );
};
